import config from '../config/api';
import {
    mockData,
    createMockResponse,
    createMockError,
    delay,
    getMockUserByRole,
    getMockDashboard,
    getMockCustomFields,
    getMockCustomFieldValues,
    getMockIncidentCustomFields,
    getMockIncidentCustomFieldValues
} from './mockData';

// Token Management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TENANT_ID_KEY = 'tenant_id';
const USER_ROLE_KEY = 'user_role'; // Store current user role for mock data
const USER_ID_KEY = 'user_id'; // Store current user ID for mock data

export const tokenManager = {
    getAccessToken: () => localStorage.getItem(TOKEN_KEY),
    setAccessToken: (token) => localStorage.setItem(TOKEN_KEY, token),
    getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
    setRefreshToken: (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
    getTenantId: () => localStorage.getItem(TENANT_ID_KEY),
    setTenantId: (id) => localStorage.setItem(TENANT_ID_KEY, id),
    getUserRole: () => localStorage.getItem(USER_ROLE_KEY),
    setUserRole: (role) => localStorage.setItem(USER_ROLE_KEY, role),
    getUserId: () => localStorage.getItem(USER_ID_KEY),
    setUserId: (id) => localStorage.setItem(USER_ID_KEY, id),
    clearTokens: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TENANT_ID_KEY);
        localStorage.removeItem(USER_ROLE_KEY);
        localStorage.removeItem(USER_ID_KEY);
    },
};

// API Client
class ApiClient {
    constructor() {
        this.baseURL = config.apiBaseUrl;
        this.isDevelopment = config.isDevelopment;
    }

    async request(endpoint, options = {}) {
        // Development Mode: Return Mock Data
        if (this.isDevelopment) {
            console.log('🔧 [DEV MODE] Using mock data for:', endpoint);
            return this.mockRequest(endpoint, options);
        }

        // Production Mode: Real API Call
        const url = `${this.baseURL}${endpoint}`;
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        // Add Authorization header
        const token = tokenManager.getAccessToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Add Tenant ID header (not required for Super Admin)
        const tenantId = tokenManager.getTenantId();
        if (tenantId) {
            headers['X-Tenant-Id'] = tenantId;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
            });

            const data = await response.json();

            if (!response.ok) {
                throw {
                    success: false,
                    message: data.message || 'Request failed',
                    code: data.code || 'ERROR',
                    details: data.details || null,
                };
            }

            return data;
        } catch (error) {
            if (error.success === false) {
                throw error;
            }
            throw {
                success: false,
                message: error.message || 'Network error',
                code: 'NETWORK_ERROR',
                details: null,
            };
        }
    }

    async mockRequest(endpoint, options = {}) {
        await delay(800); // Simulate network delay

        const method = options.method || 'GET';
        const body = options.body ? JSON.parse(options.body) : null;
        const currentRole = tokenManager.getUserRole();
        const currentUserId = tokenManager.getUserId();
        const currentTenantId = tokenManager.getTenantId();

        // Mock Authentication Endpoints
        if (endpoint === config.endpoints.auth.registerTenant && method === 'POST') {
            console.log('📝 Mock: Register Tenant', body);
            const newTenant = { ...mockData.tenant, name: body.tenantName };
            const newUser = {
                ...mockData.tenantOwners[0],
                email: body.email,
                firstName: body.firstName,
                lastName: body.lastName,
                tenant: newTenant,
            };
            tokenManager.setUserRole('TenantOwner');
            tokenManager.setUserId('1');
            tokenManager.setTenantId('1');
            return createMockResponse({
                tenant: newTenant,
                user: newUser,
                tokens: mockData.tokens,
            }, 'Tenant registered successfully');
        }

        if (endpoint === config.endpoints.auth.login && method === 'POST') {
            console.log('🔐 Mock: Login attempt', { email: body.email });

            // Determine role based on email
            let user;
            let role;
            let userId;
            let tenantId;

            if (body.email.includes('superadmin')) {
                user = { ...mockData.superAdmin };
                role = 'SuperAdmin';
                userId = 'super-admin-1';
                tenantId = null;
            } else if (body.email.includes('admin') || body.email.includes('owner')) {
                user = { ...mockData.tenantOwners[0] };
                role = 'TenantOwner';
                userId = '1';
                tenantId = '1';
            } else {
                user = { ...mockData.users[1] };
                role = 'TenantUser';
                userId = '2';
                tenantId = '1';
            }

            // Override email with login email
            user.email = body.email;

            // Store credentials for subsequent requests
            tokenManager.setUserRole(role);
            tokenManager.setUserId(userId);
            tokenManager.setTenantId(tenantId);

            return createMockResponse({
                user: user,
                tokens: {
                    accessToken: 'mock-access-token-' + Math.random().toString(36).substr(2, 9),
                    refreshToken: 'mock-refresh-token-' + Math.random().toString(36).substr(2, 9),
                },
            }, 'Mock login successful');
        }

        if (endpoint === config.endpoints.auth.refresh && method === 'POST') {
            console.log('🔄 Mock: Refresh Token');
            return createMockResponse({
                accessToken: 'mock-new-access-token-' + Math.random().toString(36).substr(2, 9),
            }, 'Token refreshed');
        }

        if (endpoint === config.endpoints.auth.logout && method === 'POST') {
            console.log('👋 Mock: Logout');
            tokenManager.clearTokens();
            return createMockResponse(null, 'Logged out successfully');
        }

        if (endpoint === config.endpoints.auth.acceptInvite && method === 'POST') {
            console.log('📧 Mock: Accept Invite', body);
            tokenManager.setUserRole('TenantUser');
            tokenManager.setUserId('2');
            tokenManager.setTenantId('1');
            return createMockResponse({
                user: { ...mockData.users[1], firstName: body.firstName, lastName: body.lastName },
                tokens: mockData.tokens,
            }, 'Invite accepted successfully');
        }

        // Mock User Endpoints
        if (endpoint === config.endpoints.user.me && method === 'GET') {
            console.log('👤 Mock: Get Current User (GET /users/me)');
            const user = getMockUserByRole(currentRole || 'TenantUser');

            // Add custom fields to user data
            const customFields = getMockCustomFieldValues(currentUserId || '2');
            const userWithCustomFields = { ...user, customFields };

            return createMockResponse(userWithCustomFields, 'User fetched successfully');
        }

        if (endpoint === config.endpoints.user.updateProfile && method === 'PATCH') {
            console.log('✏️ Mock: Update Profile', body);
            const user = getMockUserByRole(currentRole || 'TenantUser');
            const updatedUser = { ...user, ...body };
            return createMockResponse(updatedUser, 'Profile updated');
        }

        if (endpoint === config.endpoints.user.updatePassword && method === 'PATCH') {
            console.log('🔒 Mock: Update Password');
            return createMockResponse(null, 'Password updated successfully');
        }

        // Mock Tenant Endpoints
        if (endpoint === config.endpoints.tenant.current && method === 'GET') {
            console.log('🏢 Mock: Get Current Tenant (GET /tenants/current)');
            // Super Admin has no default tenant
            if (currentRole === 'SuperAdmin') {
                return createMockResponse(null, 'Super Admin has no default tenant');
            }
            return createMockResponse(mockData.tenant, 'Tenant fetched successfully');
        }

        // Mock Admin Endpoints (RBAC Applied)
        if (endpoint === config.endpoints.admin.users && method === 'GET') {
            console.log('👥 Mock: Get All Users');

            // Super Admin: See all users across all tenants
            if (currentRole === 'SuperAdmin') {
                const allUsers = [
                    mockData.superAdmin,
                    ...mockData.tenantOwners,
                    ...mockData.users,
                ];
                return createMockResponse(allUsers, 'All users fetched (Super Admin)');
            }

            // Tenant Owner: See only tenant users
            if (currentRole === 'TenantOwner') {
                return createMockResponse(mockData.users, 'Tenant users fetched');
            }

            // Tenant User: Forbidden
            return createMockError('Insufficient permissions', 'FORBIDDEN', {
                requiredRoles: ['SuperAdmin', 'TenantOwner'],
                userRole: currentRole,
            });
        }

        if (endpoint === config.endpoints.admin.tenants && method === 'GET') {
            console.log('🏢 Mock: Get All Tenants');

            // Only Super Admin can see all tenants
            if (currentRole === 'SuperAdmin') {
                return createMockResponse(mockData.tenants, 'All tenants fetched');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN', {
                requiredRoles: ['SuperAdmin'],
                userRole: currentRole,
            });
        }

        if (endpoint === config.endpoints.admin.invites && method === 'GET') {
            console.log('📨 Mock: Get All Invites');

            // Super Admin and Tenant Owner can see invites
            if (currentRole === 'SuperAdmin' || currentRole === 'TenantOwner') {
                return createMockResponse(mockData.invites, 'Invites fetched successfully');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN');
        }

        if (endpoint === config.endpoints.admin.createInvite && method === 'POST') {
            console.log('✉️ Mock: Create Invite', body);

            // Super Admin and Tenant Owner can create invites
            if (currentRole === 'SuperAdmin' || currentRole === 'TenantOwner') {
                const newInvite = {
                    ...mockData.invites[0],
                    id: Date.now().toString(),
                    email: body.email,
                    token: 'mock-invite-' + Math.random().toString(36).substr(2, 9),
                };
                return createMockResponse({ invite: newInvite, token: newInvite.token }, 'Invite created');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN');
        }

        // Mock Dashboard (Role-Based Data)
        if (endpoint === config.endpoints.dashboard.metrics && method === 'GET') {
            console.log('📊 Mock: Get Dashboard Metrics');
            const dashboard = getMockDashboard(currentRole || 'TenantUser');
            return createMockResponse(dashboard, 'Dashboard data fetched');
        }

        // ============================================
        // CUSTOM FIELDS MOCK ENDPOINTS (USER PROFILE)
        // ============================================

        // GET /custom-fields - List all custom fields for tenant (user profile)
        if (endpoint === '/custom-fields' && method === 'GET') {
            console.log('📋 Mock: Get Custom Fields');

            if (currentRole === 'SuperAdmin') {
                // Super Admin can see all custom fields
                return createMockResponse(mockData.customFields, 'All custom fields fetched');
            }

            if (currentRole === 'TenantOwner' || currentRole === 'TenantUser') {
                // Tenant users see only their tenant's custom fields
                const fields = getMockCustomFields(currentTenantId || '1');
                return createMockResponse(fields, 'Custom fields fetched');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN');
        }

        // GET /custom-fields?entityType=incident - List incident custom fields
        if (endpoint.includes('/custom-fields?entityType=incident') && method === 'GET') {
            console.log('📋 Mock: Get Incident Custom Fields');

            const fields = getMockIncidentCustomFields(currentTenantId || '1');
            return createMockResponse(fields, 'Incident custom fields fetched');
        }

        // POST /custom-fields - Create custom field
        if (endpoint === '/custom-fields' && method === 'POST') {
            console.log('➕ Mock: Create Custom Field', body);

            if (currentRole === 'SuperAdmin' || currentRole === 'TenantOwner') {
                const newField = {
                    id: mockData.customFields.length + 1,
                    tenantId: currentTenantId || '1',
                    ...body,
                    createdAt: new Date().toISOString(),
                };
                mockData.customFields.push(newField);
                return createMockResponse(newField, 'Custom field created');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN');
        }

        // GET /custom-fields/:id - Get single custom field
        if (endpoint.match(/^\/custom-fields\/\d+$/) && method === 'GET') {
            const fieldId = parseInt(endpoint.split('/')[2]);
            console.log('📄 Mock: Get Custom Field', fieldId);

            const field = mockData.customFields.find(f => f.id === fieldId);
            if (!field) {
                return createMockError('Custom field not found', 'NOT_FOUND');
            }

            // Check tenant access
            if (currentRole !== 'SuperAdmin' && field.tenantId !== currentTenantId) {
                return createMockError('Access denied', 'FORBIDDEN');
            }

            return createMockResponse(field, 'Custom field fetched');
        }

        // PATCH /custom-fields/:id - Update custom field
        if (endpoint.match(/^\/custom-fields\/\d+$/) && method === 'PATCH') {
            const fieldId = parseInt(endpoint.split('/')[2]);
            console.log('✏️ Mock: Update Custom Field', fieldId, body);

            if (currentRole === 'SuperAdmin' || currentRole === 'TenantOwner') {
                const fieldIndex = mockData.customFields.findIndex(f => f.id === fieldId);
                if (fieldIndex === -1) {
                    return createMockError('Custom field not found', 'NOT_FOUND');
                }

                mockData.customFields[fieldIndex] = {
                    ...mockData.customFields[fieldIndex],
                    ...body,
                };

                return createMockResponse(mockData.customFields[fieldIndex], 'Custom field updated');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN');
        }

        // DELETE /custom-fields/:id - Delete custom field
        if (endpoint.match(/^\/custom-fields\/\d+$/) && method === 'DELETE') {
            const fieldId = parseInt(endpoint.split('/')[2]);
            console.log('🗑️ Mock: Delete Custom Field', fieldId);

            if (currentRole === 'SuperAdmin' || currentRole === 'TenantOwner') {
                const fieldIndex = mockData.customFields.findIndex(f => f.id === fieldId);
                if (fieldIndex === -1) {
                    return createMockError('Custom field not found', 'NOT_FOUND');
                }

                mockData.customFields.splice(fieldIndex, 1);
                return createMockResponse(null, 'Custom field deleted');
            }

            return createMockError('Insufficient permissions', 'FORBIDDEN');
        }

        // POST /custom-fields/values - Assign custom field values
        if (endpoint === '/custom-fields/values' && method === 'POST') {
            console.log('💾 Mock: Assign Custom Field Values', body);

            const { entityType, entityId, userId, fields } = body;

            // For incidents, store in incident custom field values
            if (entityType === 'incident') {
                mockData.incidentCustomFieldValues[entityId] = fields;
                return createMockResponse(fields, 'Incident custom field values saved');
            }

            // For users (original functionality)
            if (userId) {
                if (currentRole === 'SuperAdmin' || currentRole === 'TenantOwner') {
                    mockData.customFieldValues[userId] = fields.map(f => {
                        const fieldDef = mockData.customFields.find(cf => cf.key === f.key);
                        return {
                            key: f.key,
                            name: fieldDef?.name || f.key,
                            type: fieldDef?.type || 'text',
                            value: f.value,
                            isRequired: fieldDef?.isRequired || false,
                        };
                    });

                    return createMockResponse(mockData.customFieldValues[userId], 'Values assigned successfully');
                }

                return createMockError('Insufficient permissions', 'FORBIDDEN');
            }

            return createMockResponse(null, 'Custom field values saved');
        }

        // GET /custom-fields/users/:userId - Get custom field values for user
        if (endpoint.match(/^\/custom-fields\/users\/[\w-]+$/) && method === 'GET') {
            const userId = endpoint.split('/')[3];
            console.log('📊 Mock: Get User Custom Field Values', userId);

            const values = getMockCustomFieldValues(userId);
            return createMockResponse(values, 'Custom field values fetched');
        }

        // GET /custom-fields/incidents/:incidentId - Get custom field values for incident
        if (endpoint.match(/^\/custom-fields\/incidents\/[\w-]+$/) && method === 'GET') {
            const incidentId = endpoint.split('/')[3];
            console.log('📊 Mock: Get Incident Custom Field Values', incidentId);

            const values = getMockIncidentCustomFieldValues(incidentId);
            return createMockResponse(values, 'Incident custom field values fetched');
        }

        // Default mock response
        console.log('❓ Mock: Unknown endpoint', endpoint);
        return createMockResponse(null, 'Mock response');
    }

    get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }

    post(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    patch(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    put(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }
}

export const api = new ApiClient();
export default api;